import React, { useState, MouseEvent } from 'react';

import { Toolbar } from '@mui/material';
import classNames from 'classnames';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button, Select, Menu, MenuItem, Checkbox } from '~/UIKit';
import { PRIMARY_BLUE, VERTICAL_SEPARATOR } from '../../../../Constants/Colors.constants';
import { IconSelect } from '../../../../Components/Icons/IconSelect';
import { TFunction } from 'i18next';
import { FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { AccessList, useHasAccess } from '~/Hooks';
import { makeStyles } from '~/UIKit/Style';

interface BookinglistToolbarProps {
  t: TFunction;
  selectedAmount: number;
  totalAmount: number;
  selectMode: boolean;
  sortOptions: any[];
  onSelectModeChanged: () => void;
  onSelectAll: () => void;
  handleSortSelect: (sort: any) => void;
  actions: any[];
  onActionSelected: (action: any) => void;
}

const useStyles = makeStyles({
  toolbar: {
    minHeight: 41,
    height: 41,
    borderTop: `1px solid ${VERTICAL_SEPARATOR}`,
    borderBottom: `1px solid ${VERTICAL_SEPARATOR}`,
  },
  selectedAmount: {
    paddingLeft: 5,
    width: '33%',
    fontWeight: 'bold',
  },
  gap: {
    flexGrow: 1,
    position: 'relative',
    height: '100%',
    '&::after': {
      content: '""',
      width: '100%',
      height: '100%',
      position: 'absolute',
      borderLeft: `1px solid ${VERTICAL_SEPARATOR}`,
      borderRight: `1px solid ${VERTICAL_SEPARATOR}`,
    },
  },
  actions: {
    height: '100%',
    width: '33%',
    borderLeft: `1px solid ${VERTICAL_SEPARATOR}`,
    borderRight: `1px solid ${VERTICAL_SEPARATOR}`,
  },
  actionButton: {
    fontSize: 12,
    width: '100%',
    height: '100%',
  },
  select: {
    fontSize: FONT_SIZE_SMALL,
    padding: '0 15px',
    height: '100%',
    fontWeight: 'bold',
  },
  selectIcon: {
    marginLeft: 4,
    marginRight: -8,
  },
  buttonSelectEnable: {
    color: PRIMARY_BLUE,
    padding: '6px 16px',
    height: '100%',
    fontSize: FONT_SIZE_SMALL,
  },
  buttonSelectDisable: {
    width: '33%',
  },
});

export default function BookinglistToolbarContainer({
  t,
  selectMode,
  selectedAmount,
  totalAmount,
  sortOptions,
  onSelectModeChanged,
  onSelectAll,
  handleSortSelect,
  onActionSelected,
  actions,
}: BookinglistToolbarProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { classes } = useStyles();
  const { hasAccess } = useHasAccess();

  if (selectMode) {
    const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = (action?: any) => {
      setAnchorEl(null);
      if (action) {
        onActionSelected(action);
      }
    };
    return (
      <Toolbar className={classes.toolbar} disableGutters>
        <div className={classes.selectedAmount}>
          <Checkbox
            checked={selectedAmount === totalAmount}
            color="primary"
            indeterminate={selectedAmount > 0 && selectedAmount < totalAmount}
            onChange={onSelectAll}
          />
          {t('app.selectedof', { selectedCount: selectedAmount, totalCount: totalAmount })}
        </div>
        <div className={classes.actions}>
          <Button
            aria-controls={'booking-actions-menu'}
            aria-haspopup
            className={classes.actionButton}
            endIcon={<KeyboardArrowDown />}
            onClick={handleMenuOpen}
          >
            {t('app.booking.actions')}
          </Button>
          <Menu
            anchorEl={anchorEl}
            id="booking-actions-menu"
            keepMounted
            onClose={() => handleClose(null)}
            open={Boolean(anchorEl)}
          >
            {actions.map((action) => (
              <MenuItem key={action.label} onClick={() => handleClose(action)}>
                {t(action.label)}
              </MenuItem>
            ))}
          </Menu>
        </div>
        <Button
          className={classNames(classes.buttonSelectEnable, classes.buttonSelectDisable)}
          onClick={onSelectModeChanged}
        >
          {t('app.checkin.select.done')}
        </Button>
      </Toolbar>
    );
  }

  return (
    <Toolbar className={classes.toolbar} disableGutters>
      {hasAccess(AccessList.MANUAL_BOOKING) && (
        <Button
          className={classes.buttonSelectEnable}
          onClick={onSelectModeChanged}
          startIcon={<IconSelect className={classes.selectIcon} />}
        />
      )}
      <div className={classes.gap} />
      <Select
        newDesign
        className={classes.select}
        data-testid="TableSortSelected"
        defaultValue={sortOptions[1].type}
        onChange={handleSortSelect}
      >
        {sortOptions.map((option, index) => (
          <MenuItem key={index} value={option.type}>
            {t(option.label)}
          </MenuItem>
        ))}
      </Select>
    </Toolbar>
  );
}
